import { Empty, Icon, message, Popover, Skeleton, Table, Tooltip } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import Button from 'components/ui/general/buttons/Button'
import './DeviceManagement.scss'
import { useDeleteVenueDevices, useListVenueDevices, useRegisterVenueDevice, useUpdateVenueDevice } from 'graphQLHooks/venue/venueManagerHooks'
import RegisterDeviceModal from '../dataEntry/other/RegisterDeviceModal'
import userMessages from 'constants/messages'
import ExpandMenuIcon from '../general/icons/ExpandMenuIcon'
import DeleteVenueDevice from '../dataEntry/other/DeleteVenueDevice'
import { cloneDeep, isEmpty } from 'lodash'
import { utilityService } from 'services/UtilityService'
import moment from 'moment'

const actionMenuOptions = [
  {
    type: 'editDevice',
    name: 'Edit Device'
  },
  {
    type: 'deleteDevice',
    name: 'Delete Device'
  }
]

const DeviceManagement = ({
  selectedCustomer,
  project,
  fetchHistory
}) => {
  const [deviceManagementTableScrollValue, setDeviceManagementTableScrollValue] = useState({ y: 'calc(100vh - 620px)' })
  const [selectedRowDetails, setSelectedRowDetails] = useState(null)
  const handleSelectedRowDetails = (details) => setSelectedRowDetails(details)
  const [isEditMode, setIsEditMode] = useState(false)
  const handleEditMode = (flag) => setIsEditMode(flag)
  const [deviceTableToggleMenuItems, setDeviceTableToggleMenuItems] = useState({})
  const [isAddDeviceModalOpen, setIsAddDeviceModalOpen] = useState(false)
  const handleAddDeviceModal = (flag) => setIsAddDeviceModalOpen(flag)
  const [isDeleteDeviceModalOpen, setIsDeleteDeviceModalOpen] = useState(false)
  const handleDeleteDeviceModal = (flag) => setIsDeleteDeviceModalOpen(flag)
  const [
    registerDevice,
    {
      loading: isRegisterDeviceLoading
    }
  ] = useRegisterVenueDevice()
  const [
    deleteVenueDevice,
    {
      loading: isDeleteVenueDeviceLoading
    }
  ] = useDeleteVenueDevices()
  const [
    updateDevice,
    {
      loading: isUpdateDeviceLoading
    }
  ] = useUpdateVenueDevice()
  const {
    loading: isListVenueDevicesLoading,
    data: venueDeviceListData,
    refetch: refetchVenueDeviceList
  } = useListVenueDevices({
    variables: {
      email: selectedCustomer
    },
    onError: (error) => {
      // message.error(userMessages.GENERAL_ERROR_MESSAGE)
      utilityService.handleError(error)
    }
  })

  const venueDeviceList = useMemo(() => {
    const details = cloneDeep(venueDeviceListData?.listVenueDevicesByEmail?.data || [])
    if (!isEmpty(details)) {
      details.sort((a, b) => {
        return moment(b.createdAt).diff(moment(a.createdAt))
      })
      return {
        listVenueDevicesByEmail: {
          ...venueDeviceListData?.listVenueDevicesByEmail,
          data: details
        }
      }
    }
    return []
  }, [venueDeviceListData])

  const submitAddDeviceModal = ({ isActive, deviceId, description, clearAllFields }) => {
    if (isEditMode) {
      updateDevice({
        variables: {
          email: selectedCustomer,
          deviceId: deviceId,
          description: description,
          active: isActive
          // platformType,
          // osVersion,
          // appVersion
        },
        onError: (error) => {
          // const errorMessage = error?.message || userMessages.GENERAL_ERROR_MESSAGE
          // message.error(errorMessage)
          utilityService.handleError(error)
        },
        onCompleted: () => {
          handleAddDeviceModal(false)
          handleEditMode(false)
          clearAllFields()
          refetchVenueDeviceList()
          // for refreshing audits
          fetchHistory()
          if (isEditMode) {
            message.success(userMessages.EDIT_VENUE_DEVICE_SUCCESS_MESSAGE)
          } else {
            message.success(userMessages.REGISTER_VENUE_DEVICE_SUCCESS_MESSAGE)
          }
        }
      })
    } else {
      registerDevice({
        variables: {
          email: selectedCustomer,
          deviceId: deviceId,
          description: description,
          active: isActive
          // platformType,
          // osVersion,
          // appVersion
        },
        onError: (error) => {
          let errorMessage = error?.message
          // from BE if UUID is not valid a stringified json is returned, get that error message if regular BE error message is not available
          try {
            const jsonParsedMessage = JSON.parse(errorMessage)
            if (!isEmpty(jsonParsedMessage)) {
              errorMessage = jsonParsedMessage?.[0]?.message
              message.error(errorMessage)
            }
          } catch (err) {
            utilityService.handleError(error)
          }
        },
        onCompleted: () => {
          handleAddDeviceModal(false)
          handleEditMode(false)
          clearAllFields()
          refetchVenueDeviceList()
          // for refreshing audits
          fetchHistory()
          if (isEditMode) {
            message.success(userMessages.EDIT_VENUE_DEVICE_SUCCESS_MESSAGE)
          } else {
            message.success(userMessages.REGISTER_VENUE_DEVICE_SUCCESS_MESSAGE)
          }
        }
      })
    }
  }

  const submitDeleteDeviceModal = (variables) => {
    deleteVenueDevice({
      variables,
      onError: (error) => {
        // const errorMessage = error?.message || userMessages.GENERAL_ERROR_MESSAGE
        // message.error(errorMessage)
        utilityService.handleError(error)
      },
      onCompleted: () => {
        // for refreshing audits
        fetchHistory()
        handleDeleteDeviceModal(false)
        refetchVenueDeviceList()
        message.success(userMessages.DELETE_VENUE_DEVICE_SUCCESS_MESSAGE)
      }
    })
  }
  const toggleActionMenu = (selectedOption) => {
    const isEditAction = selectedOption.type === 'editDevice'
    const isDeleteAction = selectedOption.type === 'deleteDevice'
    if (isEditAction) {
      setIsEditMode(true)
      handleAddDeviceModal(true)
    } else if (isDeleteAction) {
      handleDeleteDeviceModal(true)
    }
  }
  const handleOpenCloseToggleMenu = (rowId, flag) => {
    setDeviceTableToggleMenuItems(prevState => {
      let currentData = cloneDeep(prevState)
      currentData[rowId] = flag
      return currentData
    })
  }

  const columns = [
    {
      title: 'Device ID',
      dataIndex: 'id',
      key: 'id',
      render: (details) => {
        return (
          <div className='device-id'>
            <span className='device-id-text' title={details}>{details}</span>
            <Tooltip placement='topLeft' title='Copy to clipboard'>
              <span
                onClick={() => {
                  utilityService.copyToClipBoard(details, 'Device ID')
                }}
                className='copy-icon-wrapper'
              >
                <Icon type='copy' />
              </span>
            </Tooltip>
          </div>
        )
      },
      width: '22%'
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '23%'
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: (activeFlag) => (
        <> { activeFlag ? 'Yes' : 'No'}</>
      ),
      width: '5%'
    },
    {
      title: 'Last App Launched',
      dataIndex: 'lastAppLaunchAt',
      key: 'lastAppLaunchAt',
      render: (lastAppLaunchAt) => {
        return lastAppLaunchAt ? <p> {moment(lastAppLaunchAt).format('DD-MM-YYYY hh:mm:ss a')}</p> : 'null'
      },
      width: '10%'
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '6.2%',
      render: (value) => {
        return <span> {moment(value).format('DD-MM-YYYY hh:mm:ss a')}</span>
      }
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: '6.2%',
      render: (value) => {
        return <span> {moment(value).format('DD-MM-YYYY hh:mm:ss a')}</span>
      }
    },
    {
      title: 'OS Version',
      dataIndex: 'osVersion',
      key: 'osVersion',
      width: '6.2%'
    },
    {
      title: 'App Version',
      dataIndex: 'appVersion',
      key: 'appVersion',
      width: '8%'
    },
    {
      title: 'Platform Type',
      dataIndex: 'platformType',
      key: 'platformType',
      width: '9%'
    },
    {
      width: '4%',
      title: '',
      key: 'action',
      render: (details) => {
        const rowId = details?.id
        return (
          <div className='option-list' id='option-list-container'>
            <Popover
              content={
                actionMenuOptions.map(item => {
                  return (
                    <p
                      onClick={() => {
                        toggleActionMenu(item)
                        handleSelectedRowDetails(details)
                        handleOpenCloseToggleMenu(rowId, false)
                      }}
                    >
                      {item.name}
                    </p>
                  )
                })
              }
              title={null}
              trigger='click'
              visible={deviceTableToggleMenuItems?.[rowId]}
              onVisibleChange={(flag) => handleOpenCloseToggleMenu(rowId, flag)}
              placement='left'
            >
              <div><ExpandMenuIcon /></div>
            </Popover>
          </div>
        )
      }
    }
  ]

  useEffect(() => {
    const handleResize = () => {
      if (window.innerHeight < 650) {
        setDeviceManagementTableScrollValue({ y: 'calc(100vh - 208px)' })
      } else {
        setDeviceManagementTableScrollValue({ y: 'calc(100vh - 620px)' })
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <div className='device-management'>
      <div className='general-customer-details-table' id='general-customer-details-table'>
        {
          isListVenueDevicesLoading
            ? <Skeleton />
            : (
              <div className='venue-customer-table'>
                {
                  !isEmpty(venueDeviceList?.listVenueDevicesByEmail?.data)
                    ? (
                      <Table
                        columns={columns}
                        dataSource={venueDeviceList.listVenueDevicesByEmail.data}
                        bordered
                        pagination={false}
                        scroll={deviceManagementTableScrollValue}
                      />
                    )
                    : <Empty />
                }

              </div>
            )
        }
      </div>
      <div className='device-management-lower'>
        <div className='button-container'>
          <Button
            buttonText='Register Device'
            color='red'
            onClick={() => handleAddDeviceModal(true)}
            style={{ width: '150px' }}
            className='register-device-button'
            // isDisabled={isDeleteDisabled}
          />
        </div>
      </div>
      <RegisterDeviceModal
        visible={isAddDeviceModalOpen}
        onOk={submitAddDeviceModal}
        onCancel={() => {
          handleAddDeviceModal(false)
          setTimeout(() => {
            handleEditMode(false)
            handleSelectedRowDetails(null)
          }, 500)
        }}
        confirmLoading={isRegisterDeviceLoading || isUpdateDeviceLoading}
        isEditMode={isEditMode}
        fieldData={selectedRowDetails}
      />
      <DeleteVenueDevice
        visible={isDeleteDeviceModalOpen}
        onOk={submitDeleteDeviceModal}
        onCancel={() => {
          handleDeleteDeviceModal(false)
          handleSelectedRowDetails(null)
        }}
        confirmLoading={isDeleteVenueDeviceLoading}
        fieldData={selectedRowDetails}
        project={project}
      />
    </div>
  )
}

export default DeviceManagement
